import React from 'react'
import {RoomsTopOfferPrice} from 'components/Accommodation/Rooms/SplitBookingCard/RoomsTopOfferPrice'

import {Box} from '@vio/atlas/helpers/Box'
import {Offer} from '@vio/core/src/offer/types/offer'
import {getSplitBookingDates} from '@vio/core/src/room/business/utils/getSplitBookingDates'
import {getSplitBookingRooms} from '@vio/core/src/room/business/utils/getSplitBookingRooms'
import {RoomCardEntityType} from '@vio/core/src/room/types/room'

import {PrimaryRegularOfferPrice} from './PrimaryRegularOfferPrice'
import {PrimaryRoomOfferCompactLoading} from './PrimaryRoomOfferCompactLoading'
import {PrimaryRoomOfferCTA} from './PrimaryRoomOfferCTA'
import {PrimaryRoomOfferLabels} from './PrimaryRoomOfferLabels'
import {usePrimaryRoomOfferData} from './usePrimaryRoomOfferData'

interface Props {
  hotelId: string
  component: string
}

/**
 * Single-row rendering of the primary (preselected) room offer for the AP
 * sticky bottom bar on mobile: price + perks on the left, CTA on the right.
 * Shares its data with the two-column desktop PrimaryRoomOffer via
 * usePrimaryRoomOfferData.
 */
export const PrimaryRoomOfferCompact = ({hotelId, component}: Props) => {
  const {
    ref,
    isRoomsPollingWaitingForResults,
    isRoomsPollingComplete,
    pickedEntity,
    pickedOffer,
    isSplitBookingPicked,
    labelOffers,
    vioTagDetails,
    flowType,
    shouldShowFreeCancellationDate,
    trackOfferClicked
  } = usePrimaryRoomOfferData({hotelId, component})

  // Nothing to render yet also covers rooms that haven't started coming back,
  // so keep the loading state until polling says it's done.
  if (
    isRoomsPollingWaitingForResults ||
    (!pickedOffer && !isRoomsPollingComplete)
  ) {
    return <PrimaryRoomOfferCompactLoading />
  }
  if (!pickedOffer) return null

  return (
    <div ref={ref} data-id="BofhOfferCompact">
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap="s400"
      >
        <Box flexGrow="1" minWidth="0">
          {pickedEntity.type === RoomCardEntityType.SplitBooking ? (
            <RoomsTopOfferPrice
              hotelId={hotelId}
              prices={pickedOffer.prices}
              splitBookingDates={getSplitBookingDates(pickedEntity.data)}
              splitBookingRooms={getSplitBookingRooms(pickedEntity.data)}
              hidePriceDetails
              isCompact
            />
          ) : (
            <PrimaryRegularOfferPrice
              hotelId={hotelId}
              room={pickedEntity.data}
              offer={pickedOffer as Offer}
              component={component}
              isVioTagVisible={vioTagDetails.isVioTagVisible}
              isCompact
              hidePriceDetails
            />
          )}
          <PrimaryRoomOfferLabels
            shouldShowFreeCancellationDate={shouldShowFreeCancellationDate}
            offers={labelOffers}
            isCompact
            showRoomSwitchRequired={isSplitBookingPicked}
          />
        </Box>
        <PrimaryRoomOfferCTA
          trackOfferClicked={trackOfferClicked}
          flowType={flowType}
          isCompact
        />
      </Box>
    </div>
  )
}
